/**
 * 교정/범프 만기 표시 — API가 내려준 YYYY-MM-DD + cal_due_day_num / bump_due_day_num 만 사용합니다.
 * (브라우저 타임존으로 만기일을 다시 해석하거나 D-day를 재계산하지 않음)
 */

import { differenceInCalendarDays, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

const KST = 'Asia/Seoul';

/** API Date/ISO → KST 달력일 YYYY-MM-DD */
export function toKstYmd(date: Date | string): string {
  const d = typeof date === 'string' ? parseISO(date) : date;
  return formatInTimeZone(d, KST, 'yyyy-MM-dd');
}

/** KST 오늘 기준 만기일까지 남은 일수 (교정 due_day_num 과 동일 부호 규칙) */
export function dueDayNumKstFromYmd(dueYmd: string): number {
  const todayYmd = formatInTimeZone(new Date(), KST, 'yyyy-MM-dd');
  return differenceInCalendarDays(parseISO(dueYmd), parseISO(todayYmd));
}

/**
 * 구독 만료일 등 — calibration due 와 동일한 `yyyy.MM.dd (N days left)` 형식.
 */
export function formatSubscriptionExpiryLine(
  dueDate: string | Date | null | undefined,
): { text: string; dayNum: number | null } {
  if (dueDate == null || dueDate === '') {
    return { text: '-', dayNum: null };
  }
  const ymd = toKstYmd(dueDate);
  const dayNum = dueDayNumKstFromYmd(ymd);
  return {
    text: formatDueDateLine(ymd, dayNum, [1]),
    dayNum,
  };
}

/** calibration/bump interval 배열이 비었거나 전부 0이면 해당 항목 미사용 */
export function isDueIntervalDisabled(intervals: number[] | null | undefined): boolean {
  if (intervals == null || !intervals.length) {
    return true;
  }
  return intervals.every(interval => interval === 0);
}

/**
 * API가 내려준 YYYY-MM-DD(또는 그 앞부분)를 표시용 yyyy.MM.dd로만 변환합니다.
 */
function formatYmdFromServer(dueDate: string | null | undefined): string {
  if (!dueDate) return '-';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dueDate).trim());
  if (m) return `${m[1]}.${m[2]}.${m[3]}`;
  return '-';
}

/** 백엔드 `cal_due_day_num` / `bump_due_day_num`과 동일한 의미의 문구 */
function formatServerDayWord(dayNum: number): string {
  if (dayNum === 0) return 'Today';
  if (dayNum > 0) return `${dayNum} days left`;
  return `${Math.abs(dayNum)} days ago`;
}

/**
 * 기기 목록/상세: 만기일 문자열 + 서버가 계산한 남은 일수만 렌더링합니다.
 */
export function formatDueDateLine(
  dueDate: string | null | undefined,
  dayNum: number | null | undefined,
  intervals: number[] | null | undefined
): string {
  if (isDueIntervalDisabled(intervals)) {
    return '-';
  }
  if (!dueDate) {
    return '-';
  }
  const datePart = formatYmdFromServer(dueDate);
  if (dayNum === null || dayNum === undefined) {
    return datePart;
  }
  return `${datePart} (${formatServerDayWord(dayNum)})`;
}
